/**
 * Loại thông báo (HTThongBao.loaiThongBao) theo phân hệ — mirror BE
 * notification/thong-bao.constants.ts + van-hanh.constants.ts. Thứ tự = thứ tự
 * hiển thị trên màn cài đặt thông báo (giống App mobile).
 */
import routerConstants from '@/constants/routerConstants'

export const NHOM_THONG_BAO = {
  KHO: 'KHO',
  BAN_HANG: 'BAN_HANG',
} as const

export type NhomThongBao = (typeof NHOM_THONG_BAO)[keyof typeof NHOM_THONG_BAO]

export const CHE_DO_THONG_BAO = {
  BAT: 'BAT',
  TAT: 'TAT',
  TAT_DEN: 'TAT_DEN',
} as const

export type CheDoThongBao = (typeof CHE_DO_THONG_BAO)[keyof typeof CHE_DO_THONG_BAO]

/** Kênh phạm vi mảng/chi nhánh — mirror BE. */
export const KENH_THONG_BAO = {
  MOBILE: 'MOBILE',
  WEB: 'WEB',
} as const

export type KenhThongBao = (typeof KENH_THONG_BAO)[keyof typeof KENH_THONG_BAO]

export const CHE_DO_PHAM_VI_THONG_BAO = {
  BAT: 'BAT',
  TAT: 'TAT',
  TAM: 'TAM',
} as const

export type CheDoPhamViThongBao =
  (typeof CHE_DO_PHAM_VI_THONG_BAO)[keyof typeof CHE_DO_PHAM_VI_THONG_BAO]

/** Khóa cài đặt chung của App mobile (chỉ nhận push app đang mở) — web không dùng. */
export const CAI_DAT_NHAN_APP_KHAC = 'NHAN_APP_KHAC'

export const LOAI_THONG_BAO_TAT_CA = {
  CODONHANGMOI: 'CODONHANGMOI',
  CODONHANGCANNHAT: 'CODONHANGCANNHAT',
  CODONHANGCANKIEMTRA: 'CODONHANGCANKIEMTRA',
  CODONHANGCANDONGGOI: 'CODONHANGCANDONGGOI',
  CODONHANGCANDIEUPHOI: 'CODONHANGCANDIEUPHOI',
  CODONHANGCANGIAO: 'CODONHANGCANGIAO',
  DONHANG_DUOC_SUA: 'DONHANG_DUOC_SUA',
  CANHBAO_DOI_SOAT: 'CANHBAO_DOI_SOAT',
  GIAO_HANG_THANH_CONG: 'GIAO_HANG_THANH_CONG',
  GIAO_HANG_THAT_BAI: 'GIAO_HANG_THAT_BAI',
  PHIEU_THU_CHI_CHO_DUYET: 'PHIEU_THU_CHI_CHO_DUYET',
  PHIEU_THU_CHI_DA_DUYET: 'PHIEU_THU_CHI_DA_DUYET',
  PHIEU_THU_CHI_TU_CHOI: 'PHIEU_THU_CHI_TU_CHOI',
  YCX_CAN_XU_LY: 'YCX_CAN_XU_LY',
  CHUYEN_HANG_DEN: 'CHUYEN_HANG_DEN',
} as const

export type PhanHeThongBao = {
  nhom: NhomThongBao
  /** Nhóm con: key i18n `thongBao.nhomLoai.<key>` + danh sách loại. */
  groups: { key: string; loais: string[] }[]
}

const L = LOAI_THONG_BAO_TAT_CA

export const PHAN_HE_THONG_BAO: PhanHeThongBao[] = [
  {
    nhom: NHOM_THONG_BAO.KHO,
    groups: [
      {
        key: 'donHang',
        loais: [
          L.CODONHANGMOI,
          L.CODONHANGCANNHAT,
          L.CODONHANGCANKIEMTRA,
          L.CODONHANGCANDONGGOI,
          L.CODONHANGCANDIEUPHOI,
          L.CODONHANGCANGIAO,
          L.DONHANG_DUOC_SUA,
        ],
      },
      { key: 'canhBao', loais: [L.CANHBAO_DOI_SOAT] },
    ],
  },
  {
    nhom: NHOM_THONG_BAO.BAN_HANG,
    groups: [
      { key: 'giaoHang', loais: [L.GIAO_HANG_THANH_CONG, L.GIAO_HANG_THAT_BAI] },
      {
        key: 'soQuy',
        loais: [L.PHIEU_THU_CHI_CHO_DUYET, L.PHIEU_THU_CHI_DA_DUYET, L.PHIEU_THU_CHI_TU_CHOI],
      },
      { key: 'xuatHoaDon', loais: [L.YCX_CAN_XU_LY] },
      { key: 'chuyenHang', loais: [L.CHUYEN_HANG_DEN] },
    ],
  },
]

const LOAI_BAN_HANG = new Set(
  PHAN_HE_THONG_BAO.find((p) => p.nhom === NHOM_THONG_BAO.BAN_HANG)!.groups.flatMap((g) => g.loais),
)

/** KHO = mọi loại không thuộc bán hàng (giữ cả mã cũ của kho) — giống BE nhomCuaLoai. */
export const nhomCuaLoai = (loai: string): NhomThongBao =>
  LOAI_BAN_HANG.has(loai) ? NHOM_THONG_BAO.BAN_HANG : NHOM_THONG_BAO.KHO

/** Trang mở khi bấm thông báo (refId theo từng loại). null = không có trang chi tiết. */
export const duongDanThongBao = (tb: { loaiThongBao: string; refId?: string | null; title?: string }): string | null => {
  if (!tb.refId) return null
  const id = tb.refId
  switch (tb.loaiThongBao) {
    case L.GIAO_HANG_THANH_CONG:
    case L.GIAO_HANG_THAT_BAI:
      return routerConstants.hoaDon.edit.replace(':id', id)
    case L.PHIEU_THU_CHI_CHO_DUYET:
    case L.PHIEU_THU_CHI_DA_DUYET:
    case L.PHIEU_THU_CHI_TU_CHOI:
      // BE không lưu loại phiếu, tiêu đề luôn bắt đầu "Phiếu chi ..." / "Phiếu thu ..."
      return (/^phiếu chi/i.test(tb.title ?? '')
        ? routerConstants.soQuy.phieuChiDetail
        : routerConstants.soQuy.phieuThuDetail
      ).replace(':id', id)
    case L.YCX_CAN_XU_LY:
      return routerConstants.yeuCauXuatHoaDon.detail.replace(':id', id)
    case L.CHUYEN_HANG_DEN:
      return routerConstants.chuyenHang.edit.replace(':id', id)
    default:
      return routerConstants.vanHanh.detail.replace(':id', id)
  }
}
